import { useQuery } from '@tanstack/react-query';
import { useDashboard } from '@/hooks/useDashboardContext';
import { fooddb, OUTLET_TO_FOODDB, type AggregatorProvider } from '@/lib/supabase/fooddb';
import { slotsFor, type MetricConcept } from '@/lib/aggregator-metrics';
import {
  computeMetric,
  effectivePeriodRange,
  getCompareRange,
  toISTDateString,
  type DateRange,
  type Metric,
  type PeriodKey,
} from '@/lib/periods';

interface Args {
  outletId: string | 'all';
  period: PeriodKey;
  concept: MetricConcept;
  providers?: AggregatorProvider[];
  /** Default 'sum'. Use 'avg' for rate-like metrics (ROAS). */
  aggregation?: 'sum' | 'avg';
}

interface Result {
  metric: Metric;
  trend: { date: string; current: number; previous?: number }[];
  current: DateRange;
  previous: DateRange;
}

interface Row {
  date: string;
  value: number | string;
}

async function fetchRange(args: {
  outletId: string | 'all';
  concept: MetricConcept;
  providers: AggregatorProvider[];
  range: DateRange;
}): Promise<Row[]> {
  const start = toISTDateString(args.range.start);
  const end = toISTDateString(new Date(args.range.end.getTime() - 1));

  const queries: Promise<Row[]>[] = [];
  for (const provider of args.providers) {
    for (const slot of slotsFor(provider, args.concept)) {
      let q = fooddb
        .from('flax_metrics_daily')
        .select('date, value')
        .eq('provider', provider)
        .eq('category', slot.category)
        .eq('metric', slot.metric)
        .gte('date', start)
        .lte('date', end);
      if (args.outletId !== 'all') {
        const slug = OUTLET_TO_FOODDB[args.outletId];
        if (!slug) {
          queries.push(Promise.resolve([]));
          continue;
        }
        q = q.eq('outlet_id', slug);
      }
      queries.push(
        (async () => {
          const { data, error } = await q;
          if (error) throw error;
          return (data ?? []) as unknown as Row[];
        })(),
      );
    }
  }
  const parts = await Promise.all(queries);
  return parts.flat();
}

function aggregateByDate(rows: Row[], aggregation: 'sum' | 'avg') {
  const byDate = new Map<string, { sum: number; count: number }>();
  for (const r of rows) {
    const e = byDate.get(r.date) ?? { sum: 0, count: 0 };
    e.sum += Number(r.value ?? 0);
    e.count += 1;
    byDate.set(r.date, e);
  }
  const series = Array.from(byDate.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, { sum, count }]) => ({
      date,
      value: aggregation === 'avg' ? (count > 0 ? sum / count : 0) : sum,
    }));
  const total =
    aggregation === 'avg'
      ? series.length > 0
        ? series.reduce((s, p) => s + p.value, 0) / series.length
        : 0
      : series.reduce((s, p) => s + p.value, 0);
  return { series, total };
}

export function useAggregatorMetric(args: Args) {
  const { customRange } = useDashboard();
  const current = effectivePeriodRange(args.period, customRange);
  const previous = getCompareRange(current, 'previous');
  const aggregation = args.aggregation ?? 'sum';
  const providers = args.providers ?? ['swiggy', 'zomato'];

  return useQuery<Result>({
    queryKey: [
      'agg-metric',
      args.outletId,
      args.concept,
      providers.join(','),
      aggregation,
      current.start.toISOString(),
    ],
    queryFn: async () => {
      const [curRows, prevRows] = await Promise.all([
        fetchRange({ ...args, providers, range: current }),
        fetchRange({ ...args, providers, range: previous }),
      ]);
      const cur = aggregateByDate(curRows, aggregation);
      const prev = aggregateByDate(prevRows, aggregation);
      const prevMap = new Map(prev.series.map((p) => [p.date, p.value]));
      const trend = cur.series.map((p, i) => ({
        date: p.date,
        current: p.value,
        previous: prev.series[i]?.value ?? prevMap.get(p.date),
      }));
      return {
        metric: computeMetric(cur.total, prev.total),
        trend,
        current,
        previous,
      };
    },
    staleTime: 60_000,
  });
}
